import { useEffect, useRef, type ReactNode } from 'react';

/**
 * A form that needs its own space: more than a confirmation, less than a page.
 *
 * Same foundation as the confirm dialog — <dialog>.showModal(), so the focus
 * trap, Escape and the inert backdrop come from the platform rather than from
 * us reimplementing them. It is also why a confirm can be raised from INSIDE
 * one of these: the top layer stacks, and the newer dialog takes focus.
 *
 * Escape and the backdrop mean cancel, so a modal that has unsaved work in it
 * should say so in `onClose` rather than assume the close was deliberate.
 */
export function Modal({
  open,
  title,
  description,
  onClose,
  children,
  actions,
}: {
  open: boolean;
  title: string;
  description?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  actions?: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="ui-dialog ui-dialog--wide"
      onClose={onClose}
      onCancel={onClose}
      onClick={(e) => {
        // A click on the backdrop lands on the dialog itself (the inner box
        // covers everything else): that is a dismissal - on a phone, where
        // there is no Escape key, the only one besides the ✕ (2026-09-14).
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {open ? (
        <div className="ui-dialog__inner">
          <div className="ui-dialog__head">
            <h2>{title}</h2>
            {/* "Dismiss", not "Close": a form modal has its own Close button, and two controls with one name are one too many for a screen reader (and for the e2e). */}
            <button type="button" className="ui-btn ui-btn--ghost ui-btn--sm ui-dialog__close" aria-label="Dismiss" onClick={onClose}>
              ✕
            </button>
          </div>
          {description ? <div className="ui-dialog__body">{description}</div> : null}
          <div className="ui-modal__content">{children}</div>
          {actions ? <div className="ui-dialog__actions">{actions}</div> : null}
        </div>
      ) : null}
    </dialog>
  );
}
